import {formatDate} from 'date-fns';
import {
  DocumentInputField,
  DocumentModel,
  DocumentSection,
  ValidationExtraProps,
  ValidationResult,
  ValueResolver,
} from '@/engine';
import {
  DateValidation,
  DayOfTheMonthValidation,
  Validation,
  SectionValidation,
  Reference,
  InputFieldType,
} from '@/model/types';
import {parseDateExpression} from '@/model/utils/date';
import {isFieldInputType} from '@/model/utils/fields';
import {SectionValidity, Validity} from '@/types/document/store';
import {containsDangerousScript} from '@/utils/validation/sanitization';
import {mapValues} from '../object';
import {getAllFields, getVisibility} from './group';

const formatDateToLocalizedShort = (input: string) => {
  const date = parseDateExpression(input);
  const dateOnly = new Date(date.valueOf() + date.getTimezoneOffset() * 60 * 1000);
  return formatDate(dateOnly, 'yyyy-MM-dd');
};

export const mapDateError = (error: string, extra: ValidationExtraProps<DateValidation | DayOfTheMonthValidation>) => {
  const exact = extra.value && typeof extra.value === 'number' ? extra.value : undefined;
  const min = extra.value && typeof extra.value === 'object' ? extra.value.min : undefined;
  const max = extra.value && typeof extra.value === 'object' ? extra.value.max : undefined;

  if (exact) {
    return {
      error: `${error}`,
      extra: {
        value: exact,
      },
    };
  }

  if (min && max) {
    return {
      error: `${error}_range`,
      extra: {
        value: {
          min: typeof min === 'string' ? formatDateToLocalizedShort(min) : min,
          max: typeof max === 'string' ? formatDateToLocalizedShort(max) : max,
        },
      },
    };
  }

  if (min) {
    return {
      error: `${error}_min`,
      extra: {
        value: {
          min: typeof min === 'string' ? formatDateToLocalizedShort(min) : min,
        },
      },
    };
  }

  if (max) {
    return {
      error: `${error}_max`,
      extra: {
        value: {
          max: typeof max === 'string' ? formatDateToLocalizedShort(max) : max,
        },
      },
    };
  }

  return {error};
};

const mapErrors = <T extends Validation | SectionValidation>(
  results: ValidationResult<T>[],
  valueResolver: ValueResolver,
) => {
  return results
    .filter(result => !result.valid)
    .map(({error, extra}) => {
      if (!extra) return {error};

      switch (error) {
        case 'date':
        case 'dayOfTheMonth': {
          return mapDateError(error, extra);
        }

        default:
          return {
            error,
            extra: mapValues(extra, (value: unknown) => {
              if (value && typeof value === 'object') {
                if ('$ref' in value) {
                  return valueResolver(value as Reference);
                }
                if ('$query' in value && 'groupBy' in value) {
                  return [value.$query, value.groupBy + '#*'];
                }
                if ('$query' in value) {
                  return [value.$query];
                }
              }
              return value;
            }),
          };
      }
    });
};

const INPUT_FIELD_TYPES_NEEDING_CONTENT_CHECK: InputFieldType[] = ['text', 'combobox'];

const getContentNotAllowedValidationResult = (): ValidationResult<Validation> => ({
  valid: false,
  error: 'content-not-allowed',
});

export const validateField = (field: DocumentInputField, valueResolver: ValueResolver): Validity => {
  const value = valueResolver({$ref: field.id});

  const validation = field.validations.map(validation => validation(value));
  if (INPUT_FIELD_TYPES_NEEDING_CONTENT_CHECK.includes(field.type) && containsDangerousScript(value)) {
    validation.push(getContentNotAllowedValidationResult());
  }

  const errors = mapErrors(validation, valueResolver);
  const valid = errors.length === 0;

  return valid ? {valid} : {valid, errors};
};

export const validateSection = (section: DocumentSection, valueResolver: ValueResolver): SectionValidity => {
  const fieldList = section.groups.filter(getVisibility).flatMap(getAllFields);

  const fields = fieldList.reduce<Record<string, Validity>>((acc, field) => {
    if (!field.visible || !isFieldInputType(field)) return acc;
    return {...acc, [field.id]: validateField(field, valueResolver)};
  }, {});

  const sectionValidation = section.validations.map(validation => validation());

  for (const [field, validity] of Object.entries(fields)) {
    for (const sectionEntry of sectionValidation) {
      if (sectionEntry.valid || !sectionEntry.extra?.errorFields) continue;

      const fieldMatchFieldErrors = sectionEntry.extra.errorFields.find(errorField =>
        errorField.endsWith('*') ? field.startsWith(errorField.slice(0, -1)) : field === errorField,
      );

      if (!fieldMatchFieldErrors) continue;

      fields[field] = {
        ...validity,
        valid: false,
        touched: true,
        errors: [
          ...(fields[field].valid ? [] : fields[field].errors),
          {...sectionEntry, extra: {match: fieldMatchFieldErrors, section: section.id}},
        ],
      };
    }
  }

  const fieldsValid = Object.values(fields).every(field => field.valid);
  const sectionErrors = mapErrors(sectionValidation, valueResolver);
  const sectionValid = sectionErrors.length === 0;
  const valid = fieldsValid && sectionValid;

  return valid ? {valid, fields} : {valid, errors: sectionErrors, fields};
};

export const getNextSectionId = (model: DocumentModel, currentSection: string): string | undefined => {
  const currentSectionIndex = model.sections.findIndex(section => section.id === currentSection);
  const nextSection = model.sections.slice(currentSectionIndex + 1).find(section => section.visible && !section.hidden);
  return nextSection?.id;
};
